import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

// Import the webview detection library
declare const require: any;
const isWebview = require('is-ua-webview');

export interface WebviewAuthState {
  isVisible: boolean;
  returnUrl?: string;
  webviewType?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalWebviewAuthService {
  private webviewAuthState$ = new BehaviorSubject<WebviewAuthState>({ isVisible: false });

  /**
   * Observable for components to subscribe to webview auth modal state
   */
  get webviewAuthState() {
    return this.webviewAuthState$.asObservable();
  }

  /**
   * Detects if the current browser is running inside a webview
   */
  isWebview(): boolean {
    // TODO_WEBVIEW_TESTING: Change back to real detection for production
    // return true;
    return isWebview(navigator.userAgent);
  }

  /**
   * Detects specific social media webviews
   */
  getWebviewType(): string {
    const userAgent = navigator.userAgent;

    if (userAgent.includes('FBAN') || userAgent.includes('FBAV')) {
      return 'Facebook';
    }
    if (userAgent.includes('Instagram')) {
      return 'Instagram';
    }
    if (userAgent.includes('Twitter')) {
      return 'Twitter';
    }
    if (userAgent.includes('TikTok')) {
      return 'TikTok';
    }
    if (userAgent.includes('LinkedIn')) {
      return 'LinkedIn';
    }
    if (userAgent.includes('WhatsApp')) {
      return 'WhatsApp';
    }

    return 'in-app browser';
  }

  /**
   * Gets platform-specific instructions for opening in external browser
   */
  getOpenInBrowserInstructions(): string {
    const webviewType = this.getWebviewType().toLowerCase();

    switch (webviewType) {
      case 'facebook':
        return 'Tap the three dots (⋯) in the top right corner, then tap "Open in browser"';
      case 'instagram':
        return 'Tap the three dots (⋯) in the top right corner, then tap "Open in browser"';
      case 'twitter':
        return 'Tap "Open in browser" or the share button and select your browser';
      case 'tiktok':
        return 'Tap the three dots (⋯) and select "Open in browser"';
      case 'linkedin':
        return 'Tap the three dots (⋯) in the top right, then "Open in external browser"';
      case 'whatsapp':
        return 'Tap the three dots (⋯) and select "Open in browser"';
      default:
        return 'Look for "Open in browser" option in your app menu';
    }
  }

  /**
   * Shows the webview authentication warning modal
   */
  showWebviewAuthModal(returnUrl?: string): void {
    this.webviewAuthState$.next({
      isVisible: true,
      returnUrl,
      webviewType: this.getWebviewType(),
    });
  }

  /**
   * Hides the webview authentication warning modal
   */
  hideWebviewAuthModal(): void {
    this.webviewAuthState$.next({ isVisible: false });
  }

  /**
   * Checks if user should be redirected to OAuth or shown webview warning
   * Returns true if should proceed with OAuth, false if webview warning shown
   */
  checkAuthenticationFlow(returnUrl: string, oauthUrl: string): boolean {
    if (this.isWebview()) {
      // Show webview warning modal instead of redirecting to OAuth
      this.showWebviewAuthModal(returnUrl);
      return false; // Don't proceed with OAuth redirect
    }

    // Not in webview, proceed with normal OAuth flow
    window.location.href = oauthUrl;
    return true;
  }

  /**
   * Copy URL to clipboard
   */
  copyToClipboard(text: string): void {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          alert('Link copied! Please paste it in your browser.');
        })
        .catch(() => {
          this.fallbackCopyTextToClipboard(text);
        });
    } else {
      this.fallbackCopyTextToClipboard(text);
    }
  }

  private fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      alert('Link copied! Please paste it in your browser.');
    } catch (err) {
      alert('Please copy this link manually: ' + text);
    }

    document.body.removeChild(textArea);
  }
}
